import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { getCompanyMultipleConfig } from 'lib/api/company';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { getDefaultCurrentMonthRange } from 'lib/functions/dates';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  getFullTransactBookReport,
  getTransactBookReport,
} from '../../../../../../lib/api/reports';
import CustomButton from '../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import DataExporter from '../../../../../../lib/designSystem/DataExporter';
import HeaderWithActions from '../../../../../../lib/designSystem/HeaderWithActions';
import TableWithPagination from '../../../../../../lib/designSystem/TableWithPagination';
import { showAlert } from '../../../../../../lib/functions/utils';

const TransactBook: React.FC = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDownloadCompleteBook, setOpenDownloadCompleteBook] =
    useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [fullData, setFullData] = useState([]);
  const [downloadReady, setDownloadReady] = useState(false);
  const dispatch = useDispatch();
  const { startDate: startDateDefault, endDate: endDateDefault } =
    getDefaultCurrentMonthRange();
  const [startDate, setStartDate] = useState(startDateDefault);
  const [endDate, setEndDate] = useState(endDateDefault);
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [headers, setHeaders] = useState([]);
  const [fullHeaders, setFullHeaders] = useState([]);
  const [documentType, setDocumentType] = useState('factoring');
  const [showExtension, setShowExtension] = useState<string | null>(null);
  const [requestId, setRequestId] = useState('');
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (requestId) query += `requestId=${requestId}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (documentType) query += `documentType=${documentType}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const generateFullQuery = () => {
    let query = '';
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    return query;
  };
  const filters: Filter[] = [
    {
      label: 'Tipo de documento',
      type: 'select',
      value: documentType,
      setter: setDocumentType,
      options: [
        { label: 'Solicitud de factoring', value: 'factoring' },
        { label: 'Solicitud de crédito', value: 'credit' },
        { label: 'Solicitud de confirming', value: 'confirming' },
        ...(showExtension === '1'
          ? [{ label: 'Solicitud de prórroga', value: 'factoring-extension' }]
          : []),
      ],
    },
    {
      label: 'ID Solicitud',
      type: 'text',
      value: requestId,
      setter: setRequestId,
    },
    {
      label: 'Fecha de vencimiento',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de vencimiento',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },

    {
      label: 'Rut cliente',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },

    {
      label: 'Rut deudor',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
  ];
  const setUp = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setLoading(true);
    const query = generateQuery({ page, pageSize });
    try {
      const response = await getTransactBookReport(dispatch, query);
      const configurations = await getCompanyMultipleConfig(dispatch, [
        'reporting_transact_book_show_factoring_extension',
      ]);
      setData(response.data);
      setHeaders(response.headers);
      setPagination(response.pagination);
      setShowExtension(configurations);
    } catch (error) {
      console.error('Error loading transact book report:', error);
    }
    setLoading(false);
  };
  useEffect(() => {
    void setUp({ page: 0, pageSize: PAGE_SIZE });
  }, []);

  const handleDownloadCompleteBook = async () => {
    const query = generateFullQuery();
    setDownloadLoading(true);
    try {
      const response = await getFullTransactBookReport(dispatch, query);
      setFullData(response.data);
      setFullHeaders(response.headers);
      setDownloadReady(true);
    } catch (error) {
      console.error('Error downloading complete transact book:', error);
      showAlert(
        dispatch,
        'error',
        'Error al descargar el libro curse completo',
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleOpenDownloadDialog = () => {
    setFullData([]);
    setDownloadReady(false);
    setDownloadLoading(false);
    setOpenDownloadCompleteBook(true);
  };

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        title="Acá podrás ver todas las solicitudes que se han financiado."
        variant="body1"
        buttons={[
          {
            name: 'Exportar',
            action: () => handleOpenDownloadDialog(),
            color: 'primary',
          },
          {
            name: 'Filtro',
            action: () => {},
            color: 'primary',
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={() => setUp({ page: 0, pageSize: PAGE_SIZE })}
                dispatch={dispatch}
              />
            ),
          },
        ]}
      />
      <TableWithPagination
        dataHeaders={headers || []}
        data={data}
        actions={[]}
        showActions
        rowsPerPageDefault={100}
        exportToFile="xlsx"
        pagination={pagination}
        setPagination={setPagination}
        setData={setData}
        searchData={setUp}
        lastElement
        finalSumRow={true}
      />
      <Dialog
        open={openDownloadCompleteBook}
        onClose={() => setOpenDownloadCompleteBook(false)}
      >
        <DialogTitle>Descargar libro curse completo</DialogTitle>
        <DialogContent>
          {downloadLoading ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100px',
              }}
            >
              <CenteredCircularProgress relative={true} />
              <Typography>Descargando libro curse completo...</Typography>
            </div>
          ) : downloadReady ? (
            <>
              <Typography>
                Libro curse completo obtenido. Haga click en el botón para
                descargar.
              </Typography>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <DataExporter
                  data={fullData}
                  headers={fullHeaders}
                  fileType="xlsx"
                  fileName="libro-curse-completo"
                />
              </div>
            </>
          ) : (
            <>
              <Typography>
                Se obtendrán todos los tipos de solicitud.
              </Typography>
              <Typography>
                Se aplicarán los filtros de fecha y RUT seleccionados.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setOpenDownloadCompleteBook(false)}
            color="secondary"
          >
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={() => handleDownloadCompleteBook()}
            color="primary"
            disabled={downloadLoading || downloadReady}
          >
            Descargar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TransactBook;
